/**
 * POST /auth/register - Register new user
 */

import type { ActionDefinition } from '@gzl10/nexus-sdk'
import type { MiddlewareHandler } from 'hono'
import { setCookie } from 'hono/cookie'
import type { AuthService } from '../auth.service.js'
import type { RegisterInput, AuthConfig } from '../auth.types.js'
import { getCookieOptions, getRequestInfo } from './helpers.js'
import { verifyCaptcha } from '../captcha.js'

export const registerAction: ActionDefinition = {
  key: 'register',
  label: { en: 'Register', es: 'Registrarse' },
  icon: 'mdi:account-plus',
  scope: 'module',
  group: { en: 'Authentication', es: 'Autenticación' },
  method: 'POST',
  skipAuth: true,
  successStatus: 201,
  middleware: (ctx) => (ctx.services.get('authRateLimits') as { login: MiddlewareHandler }).login,
  input: {
    email: { input: 'email', required: true, label: { en: 'Email', es: 'Correo electrónico' } },
    password: { input: 'password', required: true, label: { en: 'Password', es: 'Contraseña' }, validation: { min: 8 } },
    name: { input: 'text', required: true, label: { en: 'Name', es: 'Nombre' }, validation: { min: 2 } },
    otp: { input: 'text', label: { en: 'OTP Code', es: 'Código OTP' }, validation: { min: 6, max: 6 } },
    deviceId: { input: 'text', label: { en: 'Device ID', es: 'ID de dispositivo' }, validation: { max: 64 } },
    deviceName: { input: 'text', label: { en: 'Device Name', es: 'Nombre del dispositivo' }, validation: { max: 100 } },
    captchaToken: { input: 'text', label: { en: 'CAPTCHA Token', es: 'Token CAPTCHA' }, hidden: true }
  },
  handler: async (ctx, input, c) => {
    const body = input as RegisterInput
    const authCfg = ctx.services.get<AuthConfig>('authConfig')
    const authService = ctx.services.get<AuthService>('auth')

    // CAPTCHA verification (before business logic)
    if (authCfg.captchaProvider !== 'none' && authCfg.captchaSecretKey && authCfg.captchaOnRegister) {
      const captchaError = await verifyCaptcha(body.captchaToken, {
        provider: authCfg.captchaProvider,
        secretKey: authCfg.captchaSecretKey
      }, { logger: ctx.core.logger })
      if (captchaError) throw new ctx.core.errors.ValidationError(captchaError)
    }

    const result = await authService.register(body, getRequestInfo(c, { deviceId: body.deviceId, deviceName: body.deviceName }))

    // Set refresh token in HttpOnly cookie (auto-login after register)
    if (c) setCookie(c, 'refreshToken', result.refreshToken, getCookieOptions(c, authCfg.cookieDomain))

    return {
      user: result.user,
      accessToken: result.accessToken,
      abilities: result.abilities
    }
  }
}
